import React, { useMemo } from "react";

import { matchPath, useLocation } from "react-router-dom";

import RightIcon from "@mui/icons-material/ChevronRight";
import FlagIcon from "@mui/icons-material/Flag";
import { SvgIconProps, Typography } from "@mui/material";

import Link from "@rui/Link";
import { Row } from "@rui/Stack";

import SideNavButtonIcon from "./SideNavButtonIcon";
import { SideNavButton as Container } from "./styles";

const activeStyle = {
  backgroundColor: "shades.primary.500",
  "&:hover": {
    backgroundColor: "shades.primary.500",
  },
};

interface SideNavButtonProps {
  beta?: boolean;
  label?: string;
  url?: string;
  Icon: React.FunctionComponent<SvgIconProps>;
  activeUrls?: string[];
  external?: boolean;
  open?: boolean;
  menuArrow?: boolean;
  testId?: string;
  notifications?: number;
  /** Whether the link should be "highlighted" in the side nav. Results in a more noticeable font & icon color*/
  highlight?: boolean;
  onClick?: () => void;
}

const SideNavButton = ({
  label,
  beta,
  url,
  Icon,
  activeUrls,
  external,
  open = false,
  menuArrow,
  testId,
  notifications,
  highlight = false,
  onClick,
}: SideNavButtonProps) => {
  const location = useLocation();

  const isActive = useMemo(() => {
    if (!url) return false;

    const paths = [...(activeUrls || []), url];
    return Boolean(paths.find((path) => matchPath(path, location.pathname)));
  }, [location, activeUrls, url]);

  return (
    <Link
      to={external ? undefined : url}
      href={external ? url : undefined}
      onClick={onClick}
    >
      <Container
        active={isActive || highlight}
        expanded={open}
        sx={isActive ? activeStyle : undefined}
        data-test-id={testId}
      >
        {open ? (
          <Row variant="apart" width="100%">
            <Row variant="left" spacing={1}>
              <SideNavButtonIcon
                Icon={Icon}
                notifications={notifications}
                highlight={highlight && !isActive}
              />
              <Typography
                variant="bodySecondaryReg"
                noWrap
                sx={{ fontWeight: isActive || highlight ? 500 : 400 }}
              >
                {label}
              </Typography>
            </Row>
            {beta ? (
              <FlagIcon sx={{ color: "#9996FA", fontSize: "16px" }} />
            ) : menuArrow ? (
              <RightIcon sx={{ fontSize: "16px", color: "#fff" }} />
            ) : null}
          </Row>
        ) : (
          <SideNavButtonIcon
            Icon={Icon}
            notifications={notifications}
            highlight={highlight && !isActive}
          />
        )}
      </Container>
    </Link>
  );
};

export default SideNavButton;
